import React from "react";
import PropTypes from "prop-types";
// Material imports
import Grid from "@mui/material/Grid";
import Typography from "@limitless/modern/data-display/typography/Typography";
import makeStyles from "@mui/styles/makeStyles";
import ForwardIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, Divider } from "@mui/material";
// Limitless imports
import { useExpertProfileContext } from "@limitless/expert";
import { useExpertOnboardingContext } from "@limitless/expert";
import { PortalButton, useNavigation } from "@limitless/portal";
import { recordGroupName } from "@limitless/analytics";
import Chip from "@limitless/modern/data-display/chip/Chip";
import ExpertPracticeGroupCTA from "./ExpertPracticeGroupCTA";
import ExpertWaitingListMessage from "./ExpertWaitingListMessage";
import ExpertOnboardingSummaryState from "./ExpertOnboardingSummaryState";
import ModernTenantAvatar from "../modern-task-list/modern-tenant-avatar/ModernTenantAvatar";
import { modernLogoLight } from "@limitless/images";

const useStyles = makeStyles(theme => ({
    pgCard: {
        marginTop: 12,
        position: "relative"
    },
    noLogo: {
        width: 70,
        height: 70,
        maxWidth: 70,
        maxHeight: 70,
        boxShadow: "0px 1px 8px -2px rgba(0, 0, 0, 0.25)",
        borderRadius: 12,
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        marginRight: 16
    },
    chipRed: {
        backgroundColor: theme.palette.error.light,
        position: "absolute",
        top: 0,
        right: 0,
        margin: 8,
        zIndex: 1
    },
    cta: {
        marginLeft: "auto",
        display: "flex",
        padding: "10px 16px 12px 24px",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px"
    },
    chip: {
        backgroundColor: theme.palette.secondary.light,
        position: "absolute",
        top: 0,
        right: 0,
        margin: 8,
        zIndex: 1
    },
    forwardIcon: {
        fontSize: "1rem",
        marginLeft: 8,
        marginBottom: 1,
        display: "flex",
        width: "18px",
        height: "18px",
        justifyContent: "center",
        alignItems: "center"
    },
    title: {
        // marginBottom: "10px",
        fontSize: "20px",
        fontWeight: 500,
        fontFamily: "Roboto"
    }
}));

const ExpertPracticeGroupSummaryList = ({ title, groups, ctaType = "view", isNotStartedTab = false }) => {
    const classes = useStyles();
    const { profile, profileTenantsBrandAsset } = useExpertProfileContext();
    const { navigate } = useNavigation();

    const { selectOptInGroup, getOnboardingDetail } = useExpertOnboardingContext();

    const handleViewOnboardingDetail = group => {
        getOnboardingDetail(group);
        navigate(`onboarding/detail/${group.displayName}`);
        recordGroupName(group.groupName);
    };

    const handleViewOptIn = group => {
        selectOptInGroup(group);
        navigate("onboarding/opt-in");
        recordGroupName(group.groupName);
    };

    const cta = React.useCallback(
        group => {
            let label;
            let onClick = () => handleViewOnboardingDetail(group);

            switch (ctaType) {
                case "start":
                    label = "START";
                    onClick = () => handleViewOptIn(group);
                    break;
                case "continue":
                    label = "CONTINUE";
                    break;
                default:
                    label = "SEE DETAILS";
            }

            return { label, onClick };
        },
        [ctaType, handleViewOnboardingDetail, handleViewOptIn]
    );

    if (groups.length === 0) {
        return null;
    }

    return (
        <Grid item pb={"40px"}>
            <Grid container direction="column">
                <Grid item>
                    <Typography variant="h6" className={classes.title}>
                        {title}
                    </Typography>
                    {isNotStartedTab && (
                        <Typography variant="body2" sx={{ marginBottom: 2 }}>
                            Your onboarding journey starts here. The modules below are ready for you to complete, we
                            will guide you through every step.
                        </Typography>
                    )}
                    {ctaType === "continue" && (
                        <Typography variant="body2" sx={{ marginBottom: 1 }}>
                            Your onboarding is underway! Keep going by completing the remaining steps. If you need to
                            pause and return later, your progress will be saved.
                        </Typography>
                    )}
                </Grid>

                {(isNotStartedTab || ctaType === "continue") && <Divider sx={{ paddingTop: "1%" }} />}

                <Grid item>
                    <Grid container direction="row">
                        {groups.map((g, index) => {
                            const {
                                groupName,
                                displayName,
                                onboardingOutcomeState,
                                membershipState,
                                tenantName,
                                content
                            } = g;
                            const url = profileTenantsBrandAsset.filter(item => item.tenantName === tenantName)[0]
                                ?.onboardingTenatBrandAssetUrl;

                            return (
                                <React.Fragment key={groupName}>
                                    <Grid container className={classes.pgCard} direction="column">
                                        <Grid item>
                                            <Grid container>
                                                <Grid item xs={1}>
                                                    <ModernTenantAvatar
                                                        tenantName={tenantName}
                                                        style={{
                                                            width: "65px",
                                                            height: "65px",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            backgroundColor: theme => theme.palette.background.white
                                                        }}
                                                        url={url || modernLogoLight}
                                                    />
                                                </Grid>
                                                <Grid item xs={9}>
                                                    <Grid container direction="column">
                                                        <Grid item xs={9}>
                                                            <Typography variant="subtitle1" color="textPrimary">
                                                                {displayName}
                                                            </Typography>
                                                            {(isNotStartedTab || ctaType === "continue") && (
                                                                <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                                                    {content.subHeader}
                                                                </Typography>
                                                            )}

                                                            <Box
                                                                sx={{
                                                                    position: "absolute",
                                                                    top: 8,
                                                                    right: 8,
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    alignItems: "flex-end",
                                                                    gap: 5,
                                                                    zIndex: 1
                                                                }}
                                                            >
                                                                {(onboardingOutcomeState === "rejected" ||
                                                                    onboardingOutcomeState === "promoted") &&
                                                                    ctaType !== "continue" && (
                                                                        <Chip
                                                                            label={
                                                                                onboardingOutcomeState === "rejected"
                                                                                    ? "Unsuccessful"
                                                                                    : "Successful"
                                                                            }
                                                                            sx={{
                                                                                backgroundColor:
                                                                                    onboardingOutcomeState ===
                                                                                    "rejected"
                                                                                        ? theme =>
                                                                                              theme.palette.error.light
                                                                                        : theme =>
                                                                                              theme.palette.secondary
                                                                                                  .light
                                                                            }}
                                                                        />
                                                                    )}
                                                                {ctaType !== "waiting" && (
                                                                    <Grid item>
                                                                        <PortalButton
                                                                            size="small"
                                                                            className={classes.cta}
                                                                            style={{
                                                                                marginTop: [
                                                                                    "rejected",
                                                                                    "promoted"
                                                                                ].includes(onboardingOutcomeState)
                                                                                    ? "20px"
                                                                                    : "0px"
                                                                            }}
                                                                            onClick={cta(g).onClick}
                                                                        >
                                                                            {cta(g).label}
                                                                            <ForwardIcon
                                                                                className={classes.forwardIcon}
                                                                            />
                                                                        </PortalButton>
                                                                    </Grid>
                                                                )}
                                                            </Box>
                                                        </Grid>

                                                        <Grid item mt={1}>
                                                            <ExpertOnboardingSummaryState
                                                                onboardingOutcomeState={onboardingOutcomeState}
                                                                membershipState={membershipState}
                                                                ctaType={ctaType}
                                                            />
                                                        </Grid>
                                                    </Grid>

                                                    {ctaType !== "waiting" ? (
                                                        <>
                                                            <Grid
                                                                item
                                                                component={ExpertPracticeGroupCTA}
                                                                practiceGroup={g}
                                                                isIdentityVerified={profile.idVerificationUIState}
                                                                isDocumentVerified={
                                                                    profile.documentVerification?.result
                                                                }
                                                                isPaypalVerified={profile.paypalAccount}
                                                                isNotStartedTab={isNotStartedTab}
                                                            />
                                                        </>
                                                    ) : (
                                                        <ExpertWaitingListMessage endDate={g.endDate} />
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {index < groups.length - 1 && (
                                            <Divider sx={{ marginTop: "4%", paddingTop: "2%" }} />
                                        )}
                                    </Grid>
                                </React.Fragment>
                            );
                        })}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ExpertPracticeGroupSummaryList;

ExpertPracticeGroupSummaryList.propTypes = {
    title: PropTypes.string.isRequired,
    groups: PropTypes.array.isRequired,
    ctaType: PropTypes.string,
    isNotStartedTab: PropTypes.bool
};
